import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Typography } from "@mui/material";
import CODE from "../../../../Static/Constants/StatusCodes";
import { getSOHDegradationV2, getSohRULV2 } from "../../../../Api/SpecificBatteryApi";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import BatteryHealthChartV2 from "./BatteryHealthChartV2";
import { PrimaryGranularityButton } from "../../../../Theme/DigitalTwinStyles";
import { SetBatteryHealthCycleAge } from "../../../../Actions/BatteryHealth";
import { SOH_COMPUTED_DATE_X_KEY } from "./BatteryHealthChartV2";
import { getBatteryServiceRecords } from "../../../../Api/ServiceRecordApi";
import { usePermissions } from "../../../../Context/PermissionContext";
import { buildPackReplacementTimePoints, canShowPackReplacements } from "./packReplacementAnnotations";
import { parseCalendarDateToUTC } from "../../../../Helper/DatePicker/DateConverter";

// Upper bound the service-records endpoint accepts; one battery never has more.
const REPLACEMENT_PAGE_SIZE = 500;

// Maps non-pack levels to their positional API param name
const LEVEL_NUM_PARAM = {
    string: 'stringNum',
    module: 'moduleNo',
    cell: 'cellNo',
};

// Find a node by ID anywhere in the tree
const findNodeInTree = (nodeId, tree) => {
    for (const node of tree) {
        if (node.id === nodeId) return node;
        const found = findNodeInTree(nodeId, node.children || []);
        if (found) return found;
    }
    return null;
};

// Collect all nodes at a given level via depth-first traversal
const collectNodesAtLevel = (tree, level) => {
    const result = [];
    const traverse = (nodes) => {
        for (const node of nodes) {
            if (node.level?.toLowerCase() === level) result.push(node);
            traverse(node.children || []);
        }
    };
    traverse(tree);
    return result;
};

// Build the correct API params for a node based on its level.
// Pack: uses packConfigID (systemID from battery structure, no position calc needed).
// String/module/cell: uses 1-based positional number from the tree.
const buildApiParams = (node, sidebarTree, batteryID) => {
    const level = node.level?.toLowerCase();

    if (level === 'battery') {
        return { batteryID, type: 'battery' };
    }

    if (level === 'pack') {
        return { batteryID, type: 'pack', packConfigID: node.id };
    }

    const numParam = LEVEL_NUM_PARAM[level];
    if (!numParam) {
        return { batteryID, type: 'pack', packConfigID: node.id };
    }

    const nodesAtLevel = collectNodesAtLevel(sidebarTree, level);
    const idx = nodesAtLevel.findIndex((n) => n.id === node.id);
    return { batteryID, type: level, [numParam]: idx >= 0 ? idx + 1 : 1 };
};

// Level + 1-based position among all nodes at that level; drives the fixed line color
export const getSeriesColorKey = (node, sidebarTree) => {
    const level = node.level?.toLowerCase();
    const idx = collectNodesAtLevel(sidebarTree, level).findIndex((n) => n.id === node.id);
    return { level, position: idx >= 0 ? idx + 1 : 1 };
};

const getXValue = (xKey, item, isRul = false) => {
    // Epoch ms (UTC midnight) of the computation date (sohML.date / rulForecastML.date)
    if (xKey === SOH_COMPUTED_DATE_X_KEY) return parseCalendarDateToUTC(item.date);
    if (xKey === "cycleCount") return item.eqCycleEst ?? 0;
    if (xKey === "odometer" || xKey === "latestSOHPredictionOdometer") return item.distanceTravelledKm ?? 0;
    return isRul ? (item.calendarAgeInDays ?? 0) / 30 : (item.age ?? 0) / 30;
};

const getSecondaryXValue = (xKey, item, isRul = false) => {
    if (xKey === "cycleCount") return isRul ? (item.calendarAgeInDays ?? 0) / 30 : (item.age ?? 0) / 30;
    return item.eqCycleEst ?? 0;
};

const DEFAULT_AXIS_COMBINATIONS = [
    { x: "addedOn", y: ["soh", "rul"], isDefault: true },
    { x: "cycleCount", y: ["soh", "rul"], isDefault: false },
];

export const processSeriesData = (rawSoh, rawRul, xAxisKey) => {
    const hasValidSoh = rawSoh.some((item) => item.sohEst !== null);
    const sohData = hasValidSoh
        ? rawSoh
            .map((item) =>
                item.sohEst !== null
                    ? {
                          x: getXValue(xAxisKey, item),
                          y: item.sohEst,
                          secondaryX: getSecondaryXValue(xAxisKey, item),
                          date: item.date,
                          distanceTravelledKm: item.distanceTravelledKm || 0,
                          eqCycleEst: item.eqCycleEst || 0,
                          ageMonths: (item.age || 0) / 30,
                          sohConfidence: item.sohConfidence ?? null,
                          // Raw odometer (null when missing) for the secondary-axis series; the
                          // `|| 0` field above would plot a missing reading as a drop to 0 km.
                          odometerKm: item.distanceTravelledKm ?? null,
                      }
                    : null
            )
            .filter(
                (v, i, arr) =>
                    v !== null &&
                    v.x !== null &&
                    i === arr.findIndex((t) => t !== null && t.x === v.x && t.y === v.y)
            )
        : [];

    const hasValidRul = rawRul.some((item) => item.sohForecast !== null);
    const rulData = hasValidRul
        ? rawRul
            .map((item) =>
                item.sohForecast !== null
                    ? {
                          x: getXValue(xAxisKey, item, true),
                          y: item.sohForecast,
                          secondaryX: getSecondaryXValue(xAxisKey, item, true),
                          date: item.date,
                          distanceTravelledKm: item.distanceTravelledKm || 0,
                          eqCycleEst: item.eqCycleEst || 0,
                          ageMonths: (item.calendarAgeInDays || 0) / 30,
                      }
                    : null
            )
            .filter(
                (v, i, arr) =>
                    v !== null &&
                    v.x !== null &&
                    i === arr.findIndex((t) => t !== null && t.x === v.x && t.y === v.y)
            )
        : [];

    return { sohData, rulData, hasValidSoh };
};

const BatteryHealthChartSectionV2 = ({ batteryID, selectedNode, sidebarTree }) => {
    const { t } = useTranslation(["health"]);
    const dispatch = useDispatch();
    const { batteryInfo } = useBatteryInfo();
    const pageContent = usePagesContent();

    const chartConfig = pageContent?.metaInfo?.batteryHealth?.charts?.SOHAndRUL;
    const axisCombinations = chartConfig?.axisCombinations?.length
        ? chartConfig.axisCombinations
        : DEFAULT_AXIS_COMBINATIONS;
    const tooltipFields = chartConfig?.tooltipFields || [];
    const seriesColors = chartConfig?.seriesColors;
    const cycleAgeTypeText = pageContent?.metaInfo?.translations?.eqCycle || "Eq. Cycle";

    const getAxisLabel = (xKey) => {
        const tooltipKey = xKey === "addedOn" ? "age" : xKey;
        const fromMeta = tooltipFields.find((f) => f.key === tooltipKey)?.label;
        if (fromMeta) return fromMeta;
        const fallbacks = { addedOn: "Age (Months)", cycleCount: cycleAgeTypeText, odometer: "Odometer (km)", latestSOHPredictionOdometer: "Odometer (km)", [SOH_COMPUTED_DATE_X_KEY]: "SOH Computed Date" };
        return fallbacks[xKey] || xKey;
    };

    const [xAxisKey, setXAxisKey] = useState(() => {
        const def = axisCombinations.find((c) => c.isDefault);
        return def?.x || axisCombinations[0]?.x || "addedOn";
    });

    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const showPackReplacements = canShowPackReplacements({
        tabsToShow: pageContent?.metaInfo?.tabsToShow,
        isRbacV2: isRbacV2Enabled(),
        hasPermission,
    });
    const isDateAxis = xAxisKey === SOH_COMPUTED_DATE_X_KEY;

    // Pack replacement markers (SOH Computed Date axis only). Fetched the first time
    // that axis is shown for a battery, then kept while switching axes.
    const [replacements, setReplacements] = useState({ batteryID: null, timePoints: [] });

    useEffect(() => {
        if (!isDateAxis || !showPackReplacements || !batteryID || replacements.batteryID === batteryID) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getBatteryServiceRecords(batteryID, {
                    serviceType: "PACK_REPLACEMENT",
                    pageSize: REPLACEMENT_PAGE_SIZE,
                    sortBy: "recordedAt",
                    order: "asc",
                });
                if (cancelled) return;
                // A failed or forbidden call just means no markers; the curve still renders.
                const records = Array.isArray(res?.response?.data) ? res.response.data : [];
                setReplacements({ batteryID, timePoints: buildPackReplacementTimePoints(records) });
            } catch (err) {
                console.error("Error fetching pack replacements:", err);
                if (!cancelled) setReplacements({ batteryID, timePoints: [] });
            }
        })();
        return () => { cancelled = true; };
    }, [batteryID, isDateAxis, showPackReplacements, replacements.batteryID]);

    const [chartData, setChartData] = useState({
        seriesList: [],
        responseStatus: { code: CODE.LOADING, msg: "" },
    });

    useEffect(() => {
        let cancelled = false;
        const selectedIds = Array.isArray(selectedNode) ? selectedNode : [];

        if (selectedIds.length === 0) {
            setChartData({
                seriesList: [],
                responseStatus: { code: "EMPTY_SELECTION", msg: "" },
            });
            return;
        }

        const fetchAll = async () => {
            setChartData({
                seriesList: [],
                responseStatus: { code: CODE.LOADING, msg: "" },
            });

            // Deduplicate by node ID, build API params per node's own level
            const seen = new Set();
            const nodeEntries = [];
            for (const nodeId of selectedIds) {
                if (seen.has(nodeId)) continue;
                seen.add(nodeId);

                const node = findNodeInTree(nodeId, sidebarTree || []);
                if (!node) continue;

                const label = node.label || `Item ${nodeEntries.length + 1}`;
                const apiParams = buildApiParams(node, sidebarTree || [], batteryID);
                const colorKey = getSeriesColorKey(node, sidebarTree || []);
                nodeEntries.push({ label, apiParams, colorKey });
            }

            if (nodeEntries.length === 0) {
                setChartData({
                    seriesList: [],
                    responseStatus: { code: "EMPTY_SELECTION", msg: "" },
                });
                return;
            }

            // Batch requests to avoid exhausting the browser connection pool.
            const BATCH_SIZE = 6;
            const results = [];
            for (let i = 0; i < nodeEntries.length; i += BATCH_SIZE) {
                const batch = nodeEntries.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(
                    batch.map(({ apiParams }) =>
                        Promise.all([
                            getSOHDegradationV2(apiParams),
                            getSohRULV2(apiParams),
                        ])
                    )
                );
                if (cancelled) return;
                results.push(...batchResults);
            }

            if (cancelled) return;

            if (
                results.some(([sohRes, rulRes]) =>
                    [sohRes?.responseStatus?.code, rulRes?.responseStatus?.code].includes(
                        CODE.SESSION_EXPIRED
                    )
                )
            ) {
                setChartData({
                    seriesList: [],
                    responseStatus: { code: CODE.SESSION_EXPIRED, msg: "" },
                });
                return;
            }

            const seriesList = results.map(([sohRes, rulRes], i) => {
                const { label, apiParams, colorKey } = nodeEntries[i];
                const rawSoh = sohRes?.response?.data || [];
                const rawRul = rulRes?.response?.data || [];
                const { sohData, rulData, hasValidSoh } = processSeriesData(rawSoh, rawRul, xAxisKey);
                // entityType drives per-series tooltip rules (e.g. hide battery-level SOH confidence)
                return { label, colorKey, entityType: apiParams?.type, sohData, rulData, sohActualResponse: rawSoh, rulActualResponse: rawRul, hasValidSoh };
            });

            const hasAnyValid = seriesList.some((s) => s.hasValidSoh);
            setChartData({
                seriesList,
                responseStatus: {
                    code: hasAnyValid ? CODE.SUCCESS : 400,
                    msg: hasAnyValid ? "" : "No Data Found",
                },
            });
        };

        fetchAll();
        return () => { cancelled = true; };
    }, [batteryID, selectedNode, sidebarTree, xAxisKey]);

    const updateXAxis = (xKey) => {
        if (xKey === xAxisKey) return;
        setXAxisKey(xKey);
        dispatch(SetBatteryHealthCycleAge(xKey === "cycleCount"));
    };

    // Secondary (right) y-axis series for the active x dimension, e.g. ["odometer"]
    const activeY2Keys = axisCombinations.find((c) => c.x === xAxisKey)?.y2 || [];

    return (
        <Box>
            <BatteryHealthChartV2
                data={chartData}
                cycleAgeTypeText={cycleAgeTypeText}
                tooltipFields={tooltipFields}
                searchID={batteryID}
                deviceID={batteryInfo?.deviceID}
                searchColumn={"batteryID"}
                selectedXAxisTitle={getAxisLabel(xAxisKey)}
                xAxisKey={xAxisKey}
                xAxisDimensions={axisCombinations.map((c) => c.x)}
                y2Keys={activeY2Keys}
                seriesColors={seriesColors}
                replacementTimePoints={
                    isDateAxis && showPackReplacements && replacements.batteryID === batteryID
                        ? replacements.timePoints
                        : []
                }
            />
            <Grid2 container justifyContent={"center"} sx={{ marginTop: "-15px" }}>
                <Grid2
                    container
                    sx={{ background: "#F9F9F9", padding: "4px", borderRadius: "6px" }}
                >
                    {axisCombinations.map((combo) => (
                        <PrimaryGranularityButton
                            key={combo.x}
                            disabled={false}
                            onClick={() => updateXAxis(combo.x)}
                            active={xAxisKey === combo.x}
                        >
                            {/* No textTransform — labels come from org-meta already correctly cased
                                (e.g. "Distance Travelled (km)"); capitalize would force "(Km)". */}
                            <Typography textTransform="none">{getAxisLabel(combo.x)}</Typography>
                        </PrimaryGranularityButton>
                    ))}
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default BatteryHealthChartSectionV2;
